import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {forkJoin, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, take, takeUntil} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {MatRadioChange} from '@angular/material/radio';
import {CaseHospitalDTO} from '../../../../../models/case-hospital/CaseHospitalDTO';
import {NameValueDTO} from '../../../../../../common/models/NameValueDTO';
import {LookupValue} from '../../../../../types/lookup-value.enum';
import {LookupService} from '../../../../../../common/services/lookup.service';
import {Common, deepCopy} from '../../../../../../common/util/Util';
import {CodingSchemaDTO} from 'src/app/modules/ent/models/CodingSchemaDTO';
import {DiagnosisDTO} from '../../../../../../ent/models/DiagnosisDTO';
import {ClmRequestBuilder} from '../../../../../services/clm-request-builder';
import {CodingSchemaService} from '../../../../../../ent/services/coding-schema.service';
import {DiagnosisService} from '../../../../../../ent/services/diagnosis.service';
import {SharedService} from '../../../../../../common/services/shared-service';
import {PagedResponseDTO} from 'src/app/modules/common/models/PagedResponseDTO';
import {
  ConfirmationDialogComponent
} from '../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {CaseHospitalDiagnosisLinkDTO} from '../../../../../models/case-hospital/CaseHospitalDiagnosisLinkDTO';
import {CaseHospitalCodingSchemaLinkDTO} from '../../../../../models/case-hospital/CaseHospitalCodingSchemaLinkDTO';
import {DisplayStrings} from '../../../../../../common/constants/DisplayStrings';
import {DateUtil} from '../../../../../../common/util/DateUtil';
import {SharedFormService} from '../../../../../../common/services/shared-form-service';
import {ProcessingClassSubType} from '../../../../../types/types';

// Status Step
enum StatusType {
  Yes = 'yes',
  No = 'no'
}

enum RadioGroupName {
  PrimaryDiagnosisRadioGroup = 'primaryDiagnosisRadioGroup',
  SecondaryDiagnosisRadioGroup = 'secondaryDiagnosisRadioGroup',
  PrimaryProcedureRadioGroup = 'primaryProcedureRadioGroup',
  SecondaryProcedureRadioGroup = 'secondaryProcedureRadioGroup',
}

enum DiagnosisOrProcedureType {
  PrimaryDiagnosis = 'primaryDiagnosis',
  SecondaryDiagnosis = 'secondaryDiagnosis',
  PrimaryProcedure = 'primaryProcedure',
  SecondaryProcedure = 'secondaryProcedure',
}

enum TierType {
  Primary = 'P',
  Secondary = 'S',
}

enum Controls {
  IcdCategory = 'icdCategory',
  PrimaryDiagnosisOptions = 'primaryDiagnosisOptions',
  SecondaryDiagnosisOptions = 'secondaryDiagnosisOptions',
  PrimaryProcedureOptions = 'primaryProcedureOptions',
  SecondaryProcedureOptions = 'secondaryProcedureOptions',
  PrimaryProcedureSchema = 'primaryProcedureSchema',
  SecondaryProcedureSchema = 'secondaryProcedureSchema',
  PrimaryDiagnosisDescription = 'primaryDiagnosisDescription',
  SecondaryDiagnosisDescription = 'secondaryDiagnosisDescription',
  PrimaryProcedureDescription = 'primaryProcedureDescription',
  SecondaryProcedureDescription = 'secondaryProcedureDescription',
  PrimaryDiagnosisStartDate = 'primaryDiagnosisStartDate',
  SecondaryDiagnosisStartDate = 'secondaryDiagnosisStartDate',
  PrimaryProcedureStartDate = 'primaryProcedureStartDate',
  SecondaryProcedureStartDate = 'secondaryProcedureStartDate',

  // Note: Act as Sep controls for Min validator in DatePicker
  PrimaryDiagnosisMinStartDate = 'primaryDiagnosisMinStartDate',
  SecondaryDiagnosisMinStartDate = 'secondaryDiagnosisMinStartDate',
  PrimaryProcedureMinStartDate = 'primaryProcedureMinStartDate',
  SecondaryProcedureMinStartDate = 'secondaryProcedureMinStartDate',
}

@Component({
  selector: 'app-case-admission-step',
  templateUrl: './case-admission-step.component.html',
  styleUrls: [
    '../../../../../../../../styles/global/_add-view-edit-styles.scss',
    './case-admission-step.component.scss'
  ]
})
export class CaseAdmissionStepComponent implements OnChanges, OnInit, OnDestroy {
  readonly controls = Controls;
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly displayStrings = DisplayStrings;
  readonly dateFormat = DateUtil.dateFormat;
  readonly diagnosisOrProcedureType = DiagnosisOrProcedureType;
  readonly diagnosisPrimaryDescriptionTextChanged$ = new Subject<string>();
  readonly diagnosisSecondaryDescriptionTextChanged$ = new Subject<string>();
  readonly processingClassSubTypeEnum = ProcessingClassSubType;
  readonly lookupValue = LookupValue;
  readonly radioGroupName = RadioGroupName;
  readonly statusType = StatusType;

  // Note:
  // We are creating separate subject so that on "_getFilteredListOfCodingSchemas" we can
  // explicit pass "Primary || Secondary" type which will be used in to get "procedureSchema" from dropdown
  readonly procedureDescriptionTextChangedPrimary$ = new Subject<string>();
  readonly procedureDescriptionTextChangedSecondary$ = new Subject<string>();

  procedureSchemaLookUpValues: NameValueDTO[];

  selectedPrimaryDiagnosis: DiagnosisDTO;
  selectedSecondaryDiagnosis: DiagnosisDTO;
  selectedPrimaryProcedureSchema: CodingSchemaDTO;
  selectedSecondaryProcedureSchema: CodingSchemaDTO;
  filteredDiagnosis: DiagnosisDTO[];
  filteredCodingSchema: CodingSchemaDTO[]; // Note: Procedure Schema and Coding Schema are SAME thing interchangeable words
  caseHospitalDiagnosesLinkDTOPrimaryList: CaseHospitalDiagnosisLinkDTO[] = [];
  caseHospitalDiagnosesLinkDTOSecondaryList: CaseHospitalDiagnosisLinkDTO[] = [];
  caseHospitalCodingSchemaLinkDTOPrimaryList: CaseHospitalCodingSchemaLinkDTO[] = [];
  caseHospitalCodingSchemaLinkDTOSecondaryList: CaseHospitalCodingSchemaLinkDTO[] = [];

  @Input() data: CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;
  @Input() icdCategoryFromAssociationStep: string;
  @Input() processingClassSubtype!: ProcessingClassSubType;

  @Output() updateForm: EventEmitter<FormGroup> = new EventEmitter();

  // Note: (Hack)
  // we can't use `updateForm` but that is for formControls and here
  // we are maintaining list in child therefore we need to notify to parent
  // When primary diagnosis/procedure doesn't contain at-least 1 entry
  // notify 1 parent that Step is not valid don't allow to navigate
  @Output() isValid: EventEmitter<boolean> = new EventEmitter();

  private loggedInUserName: string;
  private primaryDiagnosisRadioGroupEnabled: boolean;
  private secondaryDiagnosisRadioGroupEnabled: boolean;
  private primaryProcedureRadioGroupEnabled: boolean;
  private secondaryProcedureRadioGroupEnabled: boolean;

  constructor(private codingSchemaService: CodingSchemaService,
              private dialog: MatDialog,
              private diagnosisService: DiagnosisService,
              private formBuilder: FormBuilder,
              private lookupService: LookupService,
              private sharedService: SharedService,
              private sharedFormService: SharedFormService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      if (this.isEditMode) {
        this.setFormsForEdit(this.data);
      }
    }
    if (changes.icdCategoryFromAssociationStep && changes.icdCategoryFromAssociationStep.currentValue) {
      // Note: Will run on Funeral Case as it ICD Category available in Funeral Case
      const icdCategoryValue = changes.icdCategoryFromAssociationStep.currentValue;
      this.patchICDCategory(icdCategoryValue);
    }
  }

  ngOnInit(): void {
    this.initLookUpValues();
    this.initForm();
    this.initSubscriptions();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initLookUpValues() {
    forkJoin(
      this.lookupService.getListOfLookupValues(this.lookupValue.CodingSchemaType)
    )
      .pipe(
        take(1)
      )
      .subscribe(
        (response: any) => {
          if (response) {
            this.procedureSchemaLookUpValues = response[0];
          }
        },
        (error) => {
          console.log(error);
        });
  }

  initForm() {
    this.initFormGroupByMedicalProcessingClassSubType();
  }

  initFormGroupByMedicalProcessingClassSubType(): void {

    const commonControls = {
      [Controls.IcdCategory]: new FormControl({value: '', disabled: true}),
      [Controls.PrimaryDiagnosisOptions]: !this.isEditMode ? [this.statusType.Yes] : [this.statusType.No],
      [Controls.SecondaryDiagnosisOptions]: [this.statusType.No],
      [Controls.PrimaryDiagnosisDescription]: new FormControl(null, !this.isEditMode ? Validators.required : []),
      [Controls.SecondaryDiagnosisDescription]: new FormControl(null),
      [Controls.PrimaryDiagnosisStartDate]: new FormControl(new Date(), !this.isEditMode ? Validators.required : []),
      [Controls.SecondaryDiagnosisStartDate]: new FormControl(new Date()),
      [Controls.PrimaryDiagnosisMinStartDate]: new FormControl(null),
      [Controls.SecondaryDiagnosisMinStartDate]: new FormControl(null),
    };

    if (this.processingClassSubtype === ProcessingClassSubType.Hospital) {
      this.formGroup = this.formBuilder.group({
        ...commonControls,
        [Controls.PrimaryProcedureOptions]: [this.statusType.No],
        [Controls.SecondaryProcedureOptions]: [this.statusType.No],
        [Controls.PrimaryProcedureSchema]: new FormControl(null),
        [Controls.PrimaryProcedureDescription]: new FormControl(
          {value: null, disabled: true},
          !this.isEditMode ? Validators.required : []
        ),
        [Controls.SecondaryProcedureSchema]: new FormControl(null),
        [Controls.SecondaryProcedureDescription]: new FormControl({value: null, disabled: true}),
        [Controls.PrimaryProcedureStartDate]: new FormControl(new Date()),
        [Controls.SecondaryProcedureStartDate]: new FormControl(new Date()),
        [Controls.PrimaryProcedureMinStartDate]: new FormControl(null),
        [Controls.SecondaryProcedureMinStartDate]: new FormControl(null),
      });
    }
    else if (this.processingClassSubtype === ProcessingClassSubType.Pharmacy) {
      this.formGroup = this.formBuilder.group({
        ...commonControls,
        [Controls.PrimaryProcedureOptions]: new FormControl(null),
        [Controls.SecondaryProcedureOptions]: new FormControl(null),
        [Controls.PrimaryProcedureSchema]: new FormControl(null),
        [Controls.PrimaryProcedureDescription]: new FormControl(null),
        [Controls.SecondaryProcedureSchema]: new FormControl(null),
        [Controls.SecondaryProcedureDescription]: new FormControl(null),
        [Controls.PrimaryProcedureStartDate]: new FormControl(null),
        [Controls.SecondaryProcedureStartDate]: new FormControl(null),
        [Controls.PrimaryProcedureMinStartDate]: new FormControl(null),
        [Controls.SecondaryProcedureMinStartDate]: new FormControl(null),
      });
    }


    // Notify Updated Reference of FormGroup to parent:
    this.updateForm.next(this.formGroup);
  }

  initSubscriptions() {

    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });

    this.diagnosisPrimaryDescriptionTextChanged$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(this.debounceTime)
    )
      .subscribe((description: string) => {
          this.getFilteredListOfDiagnosis(description, true);
        },
        error => console.log(error));

    this.diagnosisSecondaryDescriptionTextChanged$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(this.debounceTime)
    )
      .subscribe((description: string) => {
          this.getFilteredListOfDiagnosis(description, false);
        },
        error => console.log(error));

    this.procedureDescriptionTextChangedPrimary$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(this.debounceTime)
    )
      .subscribe((procedureDescription: string) => {
          this.getFilteredListOfCodingSchemas(procedureDescription, DiagnosisOrProcedureType.PrimaryProcedure);
        },
        error => console.log(error));

    this.procedureDescriptionTextChangedSecondary$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(this.debounceTime)
    )
      .subscribe((procedureDescription: string) => {
          this.getFilteredListOfCodingSchemas(procedureDescription, DiagnosisOrProcedureType.SecondaryProcedure);
        },
        error => console.log(error));
  }

  // -----------------------------
  // Diagnosis Related Methods:
  // -----------------------------

  getDiagnosisDisplayString(diagnosis: DiagnosisDTO): string {
    return `${diagnosis.code}: ${diagnosis.description}`;
  }

  getCodingSchemaDisplayString(codingSchema: CodingSchemaDTO): string {
    return `${codingSchema.code}: ${codingSchema.shortDescription}`;
  }

  onDeleteHistory(data: any, type: DiagnosisOrProcedureType) {
    let name;

    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis || type === DiagnosisOrProcedureType.SecondaryDiagnosis) {
      name = data.diagnosis.description;
    }
    else if (type === DiagnosisOrProcedureType.PrimaryProcedure || type === DiagnosisOrProcedureType.SecondaryProcedure) {
      name = data.codingSchema.shortDescription;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you want to delete ${name} history?`,
      panelClass: 'white-space-none'
    });

    dialogRef.afterClosed()
      .pipe(
        take(1)
      )
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteHistoryConfirmed(data, type);
        }
        else {
          dialogRef.close();
        }
      });
  }

  onDiagnosisSelected(diagnosis: DiagnosisDTO, type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      this.selectedPrimaryDiagnosis = diagnosis;
    }
    else if (type === DiagnosisOrProcedureType.SecondaryDiagnosis) {
      this.selectedSecondaryDiagnosis = diagnosis;
    }
  }

  onUpdateDiagnosisPrimaryHistory() {
    let selectedDiagnosisDescription;
    let startDate;

    selectedDiagnosisDescription = this.formGroup.controls[Controls.PrimaryDiagnosisDescription].value;
    startDate = this.formGroup.controls[Controls.PrimaryDiagnosisStartDate].value;

    if (selectedDiagnosisDescription && startDate) {
      let newCaseDiagnosisHistory;
      let newList = [];

      newCaseDiagnosisHistory = this.getCaseHospitalDiagnosisLinkDTOPayload(DiagnosisOrProcedureType.PrimaryDiagnosis);

      if (this.isDiagnosisPrimaryHistoryExist()) {
        const firstDiagnosis = this.caseHospitalDiagnosesLinkDTOPrimaryList[0].diagnosis;
        const firstDiagnosisDescription = this.getDiagnosisDisplayString(firstDiagnosis);

        if (firstDiagnosisDescription === selectedDiagnosisDescription) {
          this.sharedService.openSnackBar(`The Primary Diagnosis description already exist`, 'Dismiss');
          return;
        }
        else {
          newList = [...this.caseHospitalDiagnosesLinkDTOPrimaryList, {...newCaseDiagnosisHistory}];
        }
      }
      else {
        newList.push(newCaseDiagnosisHistory);
      }

      newList = this.sortByColumn(newList, 'startDate');

      /*
       Note: See this comment:
       - When adding a secondary diagnosis / procedure,
       - Set start date = date specified in input.
       - Set end date = null
       - Display string must only show the start date. Ignore the end date.
       */

      newList = this.updateEndDateToPreviousDayToStartDate(newList);

      this.caseHospitalDiagnosesLinkDTOPrimaryList = newList;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.PrimaryDiagnosis);
      this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
      this.patchPrimaryDiagnosis();
      this.clearValidator(DiagnosisOrProcedureType.PrimaryDiagnosis); // Only for "Primary" since "Secondary" is not required
      this.resetSelectedPrimaryDiagnosis();
      const topDiagnosis = this.caseHospitalDiagnosesLinkDTOPrimaryList[0]?.diagnosis;
      this.patchICDCategory(this.getICDCategoryValueFromDiagnosesLinkDTOPrimaryList(topDiagnosis));
      this.isValid.next(true); // Notify parent
    }
    else {
      this.sharedService.openSnackBar(this.displayStrings.generic.requiredFields, 'Dismiss');
    }
  }

  onUpdateDiagnosisSecondaryHistory() {
    let selectedDiagnosisDescription;
    let startDate;

    selectedDiagnosisDescription = this.formGroup.controls[Controls.SecondaryDiagnosisDescription].value;
    startDate = this.formGroup.controls[Controls.SecondaryDiagnosisStartDate].value;

    if (selectedDiagnosisDescription && startDate) {
      let newCaseDiagnosisHistory;
      let newList = [];

      newCaseDiagnosisHistory = this.getCaseHospitalDiagnosisLinkDTOPayload(DiagnosisOrProcedureType.SecondaryDiagnosis);

      if (this.isDiagnosisSecondaryHistoryExist()) {
        const firstDiagnosis = this.caseHospitalDiagnosesLinkDTOSecondaryList[0].diagnosis;
        const firstDiagnosisDescription = this.getDiagnosisDisplayString(firstDiagnosis);

        if (firstDiagnosisDescription === selectedDiagnosisDescription) {
          this.sharedService.openSnackBar(`The Secondary Diagnosis description already exist`, 'Dismiss');
          return;
        }
        else {
          newList = [...this.caseHospitalDiagnosesLinkDTOSecondaryList, {...newCaseDiagnosisHistory}];
        }
      }
      else {
        newList.push(newCaseDiagnosisHistory);
      }

      newList = this.sortByColumn(newList, 'startDate');
      this.caseHospitalDiagnosesLinkDTOSecondaryList = newList;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryDiagnosis);
      this.resetSelectedSecondaryDiagnosis();
      this.patchSecondaryDiagnosis();
    }
    else {
      this.sharedService.openSnackBar(this.displayStrings.generic.requiredFields, 'Dismiss');
    }
  }

  isDiagnosisPrimaryHistoryExist(): boolean {
    return !!(this.caseHospitalDiagnosesLinkDTOPrimaryList?.length);
  }

  isDiagnosisSecondaryHistoryExist(): boolean {
    return !!(this.caseHospitalDiagnosesLinkDTOSecondaryList?.length);
  }

  // -----------------------------
  // CodingSchema Related Methods:
  // -----------------------------

  onProcedureSchemasLookUpValueSelected(type: DiagnosisOrProcedureType) {
    // Note: Disabled Procedure description until lookup value is selected:

    this.resetValuesOnProcedureSchemaChanged(type);

    if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      this.formGroup.controls[Controls.PrimaryProcedureDescription].enable();
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      this.formGroup.controls[Controls.SecondaryProcedureDescription].enable();
    }
  }

  onProcedureDescriptionSelected(codingSchemaDTO: CodingSchemaDTO, type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      this.selectedPrimaryProcedureSchema = codingSchemaDTO;
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      this.selectedSecondaryProcedureSchema = codingSchemaDTO;
    }
  }

  onUpdateProcedurePrimaryHistory() {

    let selectedProcedureSchemaDescription;
    let startDate;

    selectedProcedureSchemaDescription = this.formGroup.controls[Controls.PrimaryProcedureDescription].value;
    startDate = this.formGroup.controls[Controls.PrimaryProcedureStartDate].value;

    if (selectedProcedureSchemaDescription && startDate) {
      let newCaseProcedureSchemaHistory;
      let newList = [];

      newCaseProcedureSchemaHistory = this.getHospitalCodingSchemaLinkDTOPayload(DiagnosisOrProcedureType.PrimaryProcedure);

      if (this.isProcedurePrimaryHistoryExist()) {
        const firstProcedure = this.caseHospitalCodingSchemaLinkDTOPrimaryList[0].codingSchema;
        const firstProcedureSchemaDescription = this.getCodingSchemaDisplayString(firstProcedure);

        if (firstProcedureSchemaDescription === selectedProcedureSchemaDescription) {
          this.sharedService.openSnackBar(`The Primary Procedure Schema description already exist`, 'Dismiss');
          return;
        }
        else {
          newList = [...this.caseHospitalCodingSchemaLinkDTOPrimaryList, {...newCaseProcedureSchemaHistory}];
        }
      }
      else {
        newList.push(newCaseProcedureSchemaHistory);
      }

      newList = this.sortByColumn(newList, 'startDate');

      /*
       Note: See this comment:
       - When adding a secondary diagnosis / procedure,
       - Set start date = date specified in input.
       - Set end date = null
       - Display string must only show the start date. Ignore the end date.
       */

      newList = this.updateEndDateToPreviousDayToStartDate(newList);

      this.caseHospitalCodingSchemaLinkDTOPrimaryList = newList;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.PrimaryProcedure);
      this.patchPrimaryProcedure();
      this.formGroup.controls[Controls.PrimaryProcedureDescription].disable(); // "Disabled" since Schema is set to null
      this.resetSelectedPrimaryProcedureSchema();
      this.isValid.next(true);       // Notify parent
    }
    else {
      this.sharedService.openSnackBar(this.displayStrings.generic.requiredFields, 'Dismiss');
    }
  }

  onUpdateProcedureSecondaryHistory() {

    let selectedProcedureSchemaDescription;
    let startDate;

    selectedProcedureSchemaDescription = this.formGroup.controls[Controls.SecondaryProcedureDescription].value;
    startDate = this.formGroup.controls[Controls.SecondaryProcedureStartDate].value;

    if (selectedProcedureSchemaDescription && startDate) {
      let newCaseProcedureSchemaHistory;
      let newList = [];

      newCaseProcedureSchemaHistory = this.getHospitalCodingSchemaLinkDTOPayload(DiagnosisOrProcedureType.SecondaryProcedure);

      if (this.isProcedureSecondaryHistoryExist()) {
        const firstProcedure = this.caseHospitalCodingSchemaLinkDTOSecondaryList[0].codingSchema;
        const firstProcedureSchemaDescription = this.getCodingSchemaDisplayString(firstProcedure);

        if (firstProcedureSchemaDescription === selectedProcedureSchemaDescription) {
          this.sharedService.openSnackBar(`The Primary Procedure Schema description already exist`, 'Dismiss');
          return;
        }
        else {
          newList = [...this.caseHospitalCodingSchemaLinkDTOSecondaryList, {...newCaseProcedureSchemaHistory}];
        }
      }
      else {
        newList.push(newCaseProcedureSchemaHistory);
      }

      newList = this.sortByColumn(newList, 'startDate');

      /*
       Note: See this comment:
       - When adding a secondary diagnosis / procedure,
       - Set start date = date specified in input.
       - Set end date = null
       - Display string must only show the start date. Ignore the end date.
       */

      this.caseHospitalCodingSchemaLinkDTOSecondaryList = newList;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryProcedure);
      this.patchSecondaryProcedure();
      this.resetSelectedSecondaryProcedureSchema();
      this.formGroup.controls[Controls.SecondaryProcedureDescription].disable();
    }
    else {
      this.sharedService.openSnackBar(this.displayStrings.generic.requiredFields, 'Dismiss');
    }
  }

  isProcedurePrimaryHistoryExist(): boolean {
    return !!(this.caseHospitalCodingSchemaLinkDTOPrimaryList?.length);
  }

  isProcedureSecondaryHistoryExist(): boolean {
    return !!(this.caseHospitalCodingSchemaLinkDTOSecondaryList?.length);
  }

  // -----------------------------
  // Common Methods:
  // -----------------------------

  onUpdateRadioOption($event: MatRadioChange) {
    if ($event.source.name === RadioGroupName.PrimaryDiagnosisRadioGroup) {
      if ($event.value === this.statusType.Yes) {
        this.setValidator(DiagnosisOrProcedureType.PrimaryDiagnosis);
      }
      else {
        this.clearValidator(DiagnosisOrProcedureType.PrimaryDiagnosis);
        // Reset value in case option is disabled:
        this.patchPrimaryDiagnosis();
      }
      this.primaryDiagnosisRadioGroupEnabled = $event.value === StatusType.Yes;
    }
    else if ($event.source.name === RadioGroupName.SecondaryDiagnosisRadioGroup) {
      if ($event.value === this.statusType.Yes) {

        if (this.isDiagnosisSecondaryHistoryExist()) {
          this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryDiagnosis);
        }
        else {
          this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
        }
      }
      else if ($event.value === this.statusType.No) {
        // Reset value in case option is disabled:
        this.patchSecondaryDiagnosis();
      }
      this.secondaryDiagnosisRadioGroupEnabled = $event.value === StatusType.Yes;

    }
    else if ($event.source.name === RadioGroupName.PrimaryProcedureRadioGroup) {
      if ($event.value === this.statusType.Yes) {
        if (this.isProcedurePrimaryHistoryExist()) {
          this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.PrimaryProcedure);
        }
        else {
          this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
        }
      }
      else if ($event.value === this.statusType.No) {
        // Reset values
        this.patchPrimaryProcedure();
      }
      this.primaryProcedureRadioGroupEnabled = $event.value === StatusType.Yes;
    }
    else if ($event.source.name === RadioGroupName.SecondaryProcedureRadioGroup) {
      if ($event.value === this.statusType.Yes) {
        if (this.isProcedureSecondaryHistoryExist()) {
          this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryProcedure);
        }
        else {
          this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
        }
      }
      else if ($event.value === this.statusType.No) {
        // Reset values
        this.patchSecondaryProcedure();
      }
      this.secondaryProcedureRadioGroupEnabled = $event.value === StatusType.Yes;
    }
  }

  onBlur(type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      const value = this.formGroup.controls[Controls.PrimaryDiagnosisDescription].value;
      const isMatched = this.getSelectedDiagnosisIfSelected(value);
      if (!isMatched) {
        this.formGroup.patchValue({
          [Controls.PrimaryDiagnosisDescription]: null
        });
      }
      else {
        this.resetFilteredDiagnosis();
      }
    }
    else if (type === DiagnosisOrProcedureType.SecondaryDiagnosis) {
      const value = this.formGroup.controls[Controls.SecondaryDiagnosisDescription].value;
      const isMatched = this.getSelectedDiagnosisIfSelected(value);
      if (!isMatched) {
        this.formGroup.patchValue({
          [Controls.SecondaryDiagnosisDescription]: null
        });
      }
      else {
        this.resetFilteredDiagnosis();
      }
    }
    else if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      const value = this.formGroup.controls[Controls.PrimaryProcedureDescription].value;
      const isMatched = this.getSelectedProcedureSchemaIfSelected(value);
      if (!isMatched) {
        this.formGroup.patchValue({
          [Controls.PrimaryProcedureDescription]: null
        });
      }
      else {
        this.resetFilteredCodingSchema();
      }
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      const value = this.formGroup.controls[Controls.SecondaryProcedureDescription].value;
      const isMatched = this.getSelectedProcedureSchemaIfSelected(value);
      if (!isMatched) {
        this.formGroup.patchValue({
          [Controls.SecondaryProcedureDescription]: null
        });
      }
      else {
        this.resetFilteredCodingSchema();
      }
    }
  }

  isRadioOptionEnabled() {
    const option1 = this.formGroup.controls[Controls.PrimaryDiagnosisOptions].value === this.statusType.Yes;
    const option2 = this.formGroup.controls[Controls.SecondaryDiagnosisOptions].value === this.statusType.Yes;
    const option3 = this.formGroup.controls[Controls.PrimaryProcedureOptions].value === this.statusType.Yes;
    const option4 = this.formGroup.controls[Controls.SecondaryProcedureOptions].value === this.statusType.Yes;

    return (
      option1 || option2 || option3 || option4
    );
  }

  getPayload() {
    return {
      icdCategory: this.formGroup.get(Controls.IcdCategory).value, // This will be used in Hospital Case
      caseHospitalDiagnoses: [
        ...this.caseHospitalDiagnosesLinkDTOPrimaryList,
        ...this.caseHospitalDiagnosesLinkDTOSecondaryList
      ],
      caseHospitalCodingSchemas: [
        ...this.caseHospitalCodingSchemaLinkDTOPrimaryList,
        ...this.caseHospitalCodingSchemaLinkDTOSecondaryList
      ]
    };
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  resetAdmissionFormGroup() {
    this.formGroup.reset();
    this.resetState();
  }

  private getSelectedDiagnosisIfSelected(value: string): DiagnosisDTO {
    if (!value) {
      return;
    }
    return this.filteredDiagnosis && this.filteredDiagnosis.find((item: DiagnosisDTO) => {
      return this.getDiagnosisDisplayString(item)
        .trim() === value.trim();
    });
  }

  private getSelectedProcedureSchemaIfSelected(value: string): CodingSchemaDTO {

    if (!value) {
      return;
    }
    return this.filteredCodingSchema && this.filteredCodingSchema.find((item: CodingSchemaDTO) => {
      return this.getCodingSchemaDisplayString(item)
        .trim() === value.trim();
    });
  }


  private setValidator(type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      this.sharedFormService.setValidator(this.formGroup, Controls.PrimaryDiagnosisDescription, [Validators.required]);
      this.sharedFormService.setValidator(this.formGroup, Controls.PrimaryDiagnosisStartDate, [Validators.required]);
    }
  }

  private getCaseHospitalDiagnosisLinkDTOPayload(type: DiagnosisOrProcedureType) {
    const caseHospitalDiagnosisLinkDTO = new CaseHospitalDiagnosisLinkDTO();
    // BaseDTO fields
    caseHospitalDiagnosisLinkDTO.created = new Date();
    caseHospitalDiagnosisLinkDTO.createdBy = this.loggedInUserName;
    caseHospitalDiagnosisLinkDTO.updated = null;
    caseHospitalDiagnosisLinkDTO.updatedBy = null;
    caseHospitalDiagnosisLinkDTO.tenantId = null;

    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      caseHospitalDiagnosisLinkDTO.diagnosis = this.selectedPrimaryDiagnosis;
      caseHospitalDiagnosisLinkDTO.icdTier = TierType.Primary;
      caseHospitalDiagnosisLinkDTO.startDate = new Date(this.formGroup.get(Controls.PrimaryDiagnosisStartDate).value);
    }
    else if (type === DiagnosisOrProcedureType.SecondaryDiagnosis) {
      caseHospitalDiagnosisLinkDTO.diagnosis = this.selectedSecondaryDiagnosis;
      caseHospitalDiagnosisLinkDTO.icdTier = TierType.Secondary;
      caseHospitalDiagnosisLinkDTO.startDate = new Date(this.formGroup.get(Controls.SecondaryDiagnosisStartDate).value);
    }
    caseHospitalDiagnosisLinkDTO.endDate = null;
    return caseHospitalDiagnosisLinkDTO;
  }

  private clearValidator(type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      this.sharedFormService.clearValidator(this.formGroup, Controls.PrimaryDiagnosisDescription);
      this.sharedFormService.clearValidator(this.formGroup, Controls.PrimaryDiagnosisStartDate);
    }
  }

  private getHospitalCodingSchemaLinkDTOPayload(type: DiagnosisOrProcedureType) {
    const caseHospitalCodingSchemaLinkDTO = new CaseHospitalCodingSchemaLinkDTO();

    // BaseDTO fields
    caseHospitalCodingSchemaLinkDTO.created = new Date();
    caseHospitalCodingSchemaLinkDTO.createdBy = this.loggedInUserName;
    caseHospitalCodingSchemaLinkDTO.updated = null;
    caseHospitalCodingSchemaLinkDTO.updatedBy = null;
    caseHospitalCodingSchemaLinkDTO.tenantId = null;

    if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      caseHospitalCodingSchemaLinkDTO.codingSchema = this.selectedPrimaryProcedureSchema;
      caseHospitalCodingSchemaLinkDTO.procedureTier = TierType.Primary;
      caseHospitalCodingSchemaLinkDTO.startDate = new Date(this.formGroup.get(Controls.PrimaryProcedureStartDate).value);
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      caseHospitalCodingSchemaLinkDTO.codingSchema = this.selectedSecondaryProcedureSchema;
      caseHospitalCodingSchemaLinkDTO.procedureTier = TierType.Secondary;
      caseHospitalCodingSchemaLinkDTO.startDate = new Date(this.formGroup.get(Controls.SecondaryProcedureStartDate).value);
    }
    caseHospitalCodingSchemaLinkDTO.endDate = null;
    return caseHospitalCodingSchemaLinkDTO;
  }

  private onDeleteHistoryConfirmed(dataItem: any, type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      const index = this.caseHospitalDiagnosesLinkDTOPrimaryList.findIndex((item: CaseHospitalDiagnosisLinkDTO) => {
        return item.diagnosis.description.trim() === dataItem.diagnosis.description.trim();
      });
      this.caseHospitalDiagnosesLinkDTOPrimaryList.splice(index, 1);
      if (this.isDiagnosisPrimaryHistoryExist()) {
        this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.PrimaryDiagnosis);
      }
      else {
        this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
      }

      if (!this.isDiagnosisPrimaryHistoryExist()) {
        this.isValid.next(false);
        this.patchICDCategory(null);
      }
      else {
        this.isValid.next(true);
        const topDiagnosis = this.caseHospitalDiagnosesLinkDTOPrimaryList[0]?.diagnosis;
        this.patchICDCategory(this.getICDCategoryValueFromDiagnosesLinkDTOPrimaryList(topDiagnosis));
      }
    }
    else if (type === DiagnosisOrProcedureType.SecondaryDiagnosis) {
      const index = this.caseHospitalDiagnosesLinkDTOSecondaryList.findIndex((item: CaseHospitalDiagnosisLinkDTO) => {
        return item.diagnosis.description.trim() === dataItem.diagnosis.description.trim();
      });
      this.caseHospitalDiagnosesLinkDTOSecondaryList.splice(index, 1);

      if (this.isDiagnosisSecondaryHistoryExist()) {
        this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryDiagnosis);
      }
      else {
        this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
      }

    }
    else if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      const index = this.caseHospitalCodingSchemaLinkDTOPrimaryList.findIndex((item: CaseHospitalCodingSchemaLinkDTO) => {
        return item.codingSchema.shortDescription.trim() === dataItem.codingSchema.shortDescription.trim();
      });
      this.caseHospitalCodingSchemaLinkDTOPrimaryList.splice(index, 1);

      if (this.isProcedurePrimaryHistoryExist()) {
        this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.PrimaryProcedure);
      }
      else {
        this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
      }
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      const index = this.caseHospitalCodingSchemaLinkDTOSecondaryList.findIndex((item: CaseHospitalCodingSchemaLinkDTO) => {
        return item.codingSchema.shortDescription.trim() === dataItem.codingSchema.shortDescription.trim();
      });
      this.caseHospitalCodingSchemaLinkDTOSecondaryList.splice(index, 1);

      if (this.isProcedureSecondaryHistoryExist()) {
        this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryProcedure);
      }
      else {
        this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
      }
    }
  }

  private resetValuesOnProcedureSchemaChanged(type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      this.resetSelectedPrimaryProcedureSchema();
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      this.resetSelectedSecondaryProcedureSchema();
    }
    this.resetFilteredCodingSchema();
  }

  private resetState() {
    this.resetSelectedPrimaryDiagnosis();
    this.resetSelectedSecondaryDiagnosis();
    this.resetSelectedPrimaryProcedureSchema();
    this.resetSelectedSecondaryProcedureSchema();
    this.resetFilteredDiagnosis();
    this.resetFilteredCodingSchema();
  }

  private resetSelectedPrimaryDiagnosis() {
    this.selectedPrimaryDiagnosis = null;
  }

  private resetSelectedSecondaryDiagnosis() {
    this.selectedSecondaryDiagnosis = null;
  }

  private resetSelectedPrimaryProcedureSchema() {
    this.selectedPrimaryProcedureSchema = null;
  }

  private resetSelectedSecondaryProcedureSchema() {
    this.selectedSecondaryProcedureSchema = null;
  }

  private resetFilteredDiagnosis() {
    this.filteredDiagnosis = [];
  }

  private resetFilteredCodingSchema() {
    this.filteredCodingSchema = [];
  }

  private patchICDCategory(categoryValue: string) {
    this.formGroup.patchValue({
      [Controls.IcdCategory]: categoryValue
    });
  }

  private getICDCategoryValueFromDiagnosesLinkDTOPrimaryList(diagnosis: DiagnosisDTO): string {
    return `${diagnosis.category} (${diagnosis.categoryNumber})`;
  }

  private patchPrimaryDiagnosis() {
    this.formGroup.patchValue({
      [Controls.PrimaryDiagnosisOptions]: this.statusType.No,
      [Controls.PrimaryDiagnosisDescription]: null,
      [Controls.PrimaryDiagnosisStartDate]: null
    });
  }

  private patchSecondaryDiagnosis() {
    this.formGroup.patchValue({
      [Controls.SecondaryDiagnosisOptions]: this.statusType.No,
      [Controls.SecondaryDiagnosisDescription]: null,
      [Controls.SecondaryDiagnosisStartDate]: null
    });
  }

  private patchPrimaryProcedure() {
    this.formGroup.patchValue({
      [Controls.PrimaryProcedureOptions]: this.statusType.No,
      [Controls.PrimaryProcedureSchema]: null,
      [Controls.PrimaryProcedureDescription]: null,
      [Controls.PrimaryProcedureStartDate]: null
    });
  }

  private patchSecondaryProcedure() {
    this.formGroup.patchValue({
      [Controls.SecondaryProcedureOptions]: this.statusType.No,
      [Controls.SecondaryProcedureSchema]: null,
      [Controls.SecondaryProcedureDescription]: null,
      [Controls.SecondaryProcedureStartDate]: null
    });
  }

  private setFormsForEdit(data: CaseHospitalDTO) {
    this.setCaseHospitalDiagnoses(data);
    this.setCaseHospitalCodingSchemas(data);
  }

  private setCaseHospitalDiagnoses(data: CaseHospitalDTO) {
    const {caseHospitalDiagnoses} = data;
    let primaryList = [];
    let secondary = [];

    if (caseHospitalDiagnoses?.length) {
      primaryList = caseHospitalDiagnoses.filter(item => item.icdTier === TierType.Primary);
      secondary = caseHospitalDiagnoses.filter(item => item.icdTier === TierType.Secondary);
    }

    // Note: List is differentiated between "icdTier" type either Primary | Secondary
    this.setDiagnosesPrimaryList(primaryList);
    this.setDiagnosesSecondaryList(secondary);
  }

  private setDiagnosesPrimaryList(primaryList: CaseHospitalDiagnosisLinkDTO[]) {
    if (primaryList && primaryList.length > 0) {
      let list;
      this.sortByColumn(primaryList, 'startDate');
      list = this.updateEndDateToPreviousDayToStartDate(primaryList);
      this.caseHospitalDiagnosesLinkDTOPrimaryList = list;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.PrimaryDiagnosis);
      this.handleEdgeCaseBasedOnPrimaryDiagnosis(DiagnosisOrProcedureType.PrimaryDiagnosis);
      const topDiagnosis = this.caseHospitalDiagnosesLinkDTOPrimaryList[0]?.diagnosis;
      this.patchICDCategory(this.getICDCategoryValueFromDiagnosesLinkDTOPrimaryList(topDiagnosis));

      // Notify parent
      // Since in edit mode we should have at least 1 entry
      this.isValid.next(true);
    }
    else {
      this.caseHospitalDiagnosesLinkDTOPrimaryList = [];
    }
  }

  private setDiagnosesSecondaryList(secondaryList: CaseHospitalDiagnosisLinkDTO[]) {
    if (secondaryList && secondaryList.length > 0) {
      let list;

      this.sortByColumn(secondaryList, 'startDate');
      list = this.updateEndDateToPreviousDayToStartDate(secondaryList);
      this.caseHospitalDiagnosesLinkDTOSecondaryList = list;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryDiagnosis);
    }
    else {
      this.caseHospitalDiagnosesLinkDTOSecondaryList = [];
    }
  }

  private setCaseHospitalCodingSchemas(data: CaseHospitalDTO) {
    const {caseHospitalCodingSchemas} = data;
    let primaryList = [];
    let secondary = [];

    if (caseHospitalCodingSchemas?.length) {
      primaryList = caseHospitalCodingSchemas.filter(item => item.procedureTier === TierType.Primary);
      secondary = caseHospitalCodingSchemas.filter(item => item.procedureTier === TierType.Secondary);
    }
    // Note: List is differentiated between "procedureTier" type either Primary | Secondary
    this.setProcedurePrimaryList(primaryList);
    this.setProcedureSecondaryList(secondary);
  }

  private setProcedurePrimaryList(primaryList: CaseHospitalCodingSchemaLinkDTO[]) {
    if (primaryList && primaryList.length > 0) {
      let list;
      this.sortByColumn(primaryList, 'startDate');
      list = this.updateEndDateToPreviousDayToStartDate(primaryList);
      this.caseHospitalCodingSchemaLinkDTOPrimaryList = list;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.PrimaryProcedure);

      // Notify parent
      // Since in edit mode we should have at least 1 entry
      this.isValid.next(true);
    }
    else {
      this.caseHospitalCodingSchemaLinkDTOPrimaryList = [];
    }
  }

  private setProcedureSecondaryList(secondaryList: CaseHospitalCodingSchemaLinkDTO[]) {
    if (secondaryList && secondaryList.length > 0) {
      let list;

      this.sortByColumn(secondaryList, 'startDate');
      list = this.updateEndDateToPreviousDayToStartDate(secondaryList);
      this.caseHospitalCodingSchemaLinkDTOSecondaryList = list;
      this.setDateAndMinStartDateForDatepicker(DiagnosisOrProcedureType.SecondaryProcedure);
    }
    else {
      this.caseHospitalCodingSchemaLinkDTOSecondaryList = [];
    }
  }

  private sortByColumn(list, columnName: string) {
    return list.sort((firstElement, secondElement) => {
      return new Date(secondElement[columnName]).getTime() - new Date(firstElement[columnName]).getTime();
    });
  }

  private setDateAndMinStartDateForDatepicker(type: DiagnosisOrProcedureType) {
    // Order is important as it should be after list is sorted based on `startDate`
    // The "minDate" property on the date picker should be "1" day greater than the latest "status" startDate

    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      const listExits = !!this.isDiagnosisPrimaryHistoryExist();
      const list = listExits ? this.caseHospitalDiagnosesLinkDTOPrimaryList : [];
      const latestDateToSet = this.getTopHistoryStartDateForDatePicker(list);
      this.formGroup.patchValue({
        [Controls.PrimaryDiagnosisStartDate]: latestDateToSet,
        [Controls.PrimaryDiagnosisMinStartDate]: !listExits ? null : latestDateToSet
      });
    }
    else if (type === DiagnosisOrProcedureType.SecondaryDiagnosis) {
      const listExits = !!this.isDiagnosisSecondaryHistoryExist();
      const list = listExits ? this.caseHospitalDiagnosesLinkDTOSecondaryList : [];
      const latestDateToSet = this.getTopHistoryStartDateForDatePicker(list);
      this.formGroup.patchValue({
        [Controls.SecondaryDiagnosisStartDate]: latestDateToSet,
        [Controls.SecondaryDiagnosisMinStartDate]: !listExits ? null : latestDateToSet
      });
    }
    else if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      const listExits = !!this.isProcedurePrimaryHistoryExist();
      const list = listExits ? this.caseHospitalCodingSchemaLinkDTOPrimaryList : [];
      const latestDateToSet = this.getTopHistoryStartDateForDatePicker(list);
      this.formGroup.patchValue({
        [Controls.PrimaryProcedureStartDate]: latestDateToSet,
        [Controls.PrimaryProcedureMinStartDate]: !listExits ? null : latestDateToSet
      });
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      const listExits = !!this.isProcedureSecondaryHistoryExist();
      const list = listExits ? this.caseHospitalCodingSchemaLinkDTOSecondaryList : [];
      const latestDateToSet = this.getTopHistoryStartDateForDatePicker(list);
      this.formGroup.patchValue({
        [Controls.SecondaryProcedureStartDate]: latestDateToSet,
        [Controls.SecondaryProcedureMinStartDate]: !listExits ? null : latestDateToSet
      });
    }
  }

  private getTopHistoryStartDateForDatePicker(list: CaseHospitalDiagnosisLinkDTO[] | CaseHospitalCodingSchemaLinkDTO[]): Date {
    let latestDateToSet;
    if (list && list?.length) {
      const topHistory = list[0];
      latestDateToSet = this.getLatestDateToSet(topHistory)?.tomorrow;
    }
    else {
      latestDateToSet = new Date();
    }
    return latestDateToSet;
  }

  private getLatestDateToSet(item: CaseHospitalDiagnosisLinkDTO | CaseHospitalCodingSchemaLinkDTO): any {
    /*
     Note: Edge Case
     In case of multiple status once

     statusName: "startDate - "         =>  TOP status is "REMOVED"
     statusName: "startDate - endDate"  => SECOND status

     Important! Calendar "minDate" should be endDate + 1 from -> SECOND status
     */

    let latestStartDateToSet;
    let currentDate;
    if (item.startDate && item.endDate) {
      currentDate = new Date(item.endDate);
      latestStartDateToSet = new Date(new Date(item.endDate).setDate(new Date(item.endDate).getDate() + 1));
    }
    else {
      currentDate = new Date(item.startDate);
      latestStartDateToSet = new Date(new Date(item.startDate).setDate(new Date(item.startDate).getDate() + 1));
    }

    return {
      currentDate,
      tomorrow: latestStartDateToSet
    };
  }

  private updateEndDateToPreviousDayToStartDate(list) {
    // Important Note: In case of 1 entry end date should be "null"
    // template -> : {{startDate}}"-"{{endDate}}

    const newList: any[] = deepCopy(list);

    if (newList.length > 1) {
      for (let index = 0; index < newList.length - 1; index++) {
        // Note: -1 on date after "-" should be date selected -1 (previous day)

        newList[index + 1].endDate =
          new Date(new Date(newList[index].startDate).setDate(new Date(newList[index].startDate).getDate() - 1));
      }
    }
    return newList;
  }

  private getFilteredListOfDiagnosis(value: string, primaryOnly: boolean): void {
    const byDescriptionPagedRequestDTO = ClmRequestBuilder.getListOfDiagnosisByDescriptionPagedRequestDTO(value, 100, primaryOnly);
    const byCodePagedRequestDTO = ClmRequestBuilder.getListOfDiagnosisByCodePagedRequestDTO(value, 100, primaryOnly);

    forkJoin(
      this.diagnosisService.getListOfDiagnoses(byDescriptionPagedRequestDTO),
      this.diagnosisService.getListOfDiagnoses(byCodePagedRequestDTO)
    )
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe((responses: [PagedResponseDTO<DiagnosisDTO>, PagedResponseDTO<DiagnosisDTO>]) => {
          this.filteredDiagnosis = responses[0].resultList.concat(responses[1].resultList);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getFilteredListOfCodingSchemas(value: string, type: DiagnosisOrProcedureType): void {
    let procedureSchema;
    let selectedProcedureSchema;

    if (type === DiagnosisOrProcedureType.PrimaryProcedure) {
      selectedProcedureSchema = this.formGroup.controls[Controls.PrimaryProcedureSchema].value;
    }
    else if (type === DiagnosisOrProcedureType.SecondaryProcedure) {
      selectedProcedureSchema = this.formGroup.controls[Controls.SecondaryProcedureSchema].value;
    }

    procedureSchema = this.procedureSchemaLookUpValues.find((item: NameValueDTO) => item.value === selectedProcedureSchema).name;

    const byDescriptionPagedRequestDTO = ClmRequestBuilder.getListOfCodingSchemaByDescriptionPagedRequestDTO(value, procedureSchema, 100);
    const byCodePagedRequestDTO = ClmRequestBuilder.getListOfCodingSchemaByCodePagedRequestDTO(value, procedureSchema, 100);

    forkJoin(
      this.codingSchemaService.getListOfCodingSchemas(byDescriptionPagedRequestDTO),
      this.codingSchemaService.getListOfCodingSchemas(byCodePagedRequestDTO)
    )
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe((responses: [PagedResponseDTO<CodingSchemaDTO>, PagedResponseDTO<CodingSchemaDTO>]) => {
          this.filteredCodingSchema = responses[0].resultList.concat(responses[1].resultList);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private handleEdgeCaseBasedOnPrimaryDiagnosis(type: DiagnosisOrProcedureType) {
    if (type === DiagnosisOrProcedureType.PrimaryDiagnosis) {
      // Note: 2 columns (primary/secondary) procedure to use the start date of the latest primary diagnosis.
      const listExits = !!this.isDiagnosisPrimaryHistoryExist();
      const list = listExits ? this.caseHospitalDiagnosesLinkDTOPrimaryList : [];

      let latestDateToSet;

      if (list?.length && list?.length) {
        const topHistory = list[0];
        latestDateToSet = this.getLatestDateToSet(topHistory)?.currentDate;
      }
      else {
        latestDateToSet = new Date();
      }

      if (!this.isDiagnosisSecondaryHistoryExist() && !this.secondaryDiagnosisRadioGroupEnabled) {
        this.formGroup.patchValue({
          [Controls.SecondaryDiagnosisStartDate]: latestDateToSet,
          [Controls.SecondaryDiagnosisMinStartDate]: null
        });
      }
      if (!this.isProcedurePrimaryHistoryExist() && !this.primaryProcedureRadioGroupEnabled) {
        this.formGroup.patchValue({
          [Controls.PrimaryProcedureStartDate]: latestDateToSet,
          [Controls.PrimaryProcedureMinStartDate]: null
        });
      }
      if (!this.isProcedureSecondaryHistoryExist() && !this.secondaryProcedureRadioGroupEnabled) {
        this.formGroup.patchValue({
          [Controls.SecondaryProcedureStartDate]: latestDateToSet,
          [Controls.SecondaryProcedureMinStartDate]: null
        });
      }
    }
  }
}
